import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { hasNoReviews, NoReviewsYetBadge } from '../components/NoReviewsYetBadge'
import { Agency } from '../models/Agency.model'
import { Hospital } from '../models/Hospital.model'
import { USStates } from '../models/USStates.model'
import { AgencyService } from '../services/AgencyService/AgencyService'
import { HospitalService } from '../services/HospitalService/HospitalService'

type DirectoryTab = 'hospitals' | 'agencies'

export type DirectorySearchLocationState = {
  initialQuery?: string
  cityStateSearchTerm?: string
}

function formatStateEnumKey(key: string): string {
  return key.replace(/([a-z])([A-Z])/g, '$1 $2')
}

function parseCityStateForHospitalSearch(cityStateSearchTerm: string) {
  const cityStateParts = cityStateSearchTerm?.split(',') ?? []
  const stateString = !cityStateParts.length ? undefined : cityStateParts[cityStateParts.length - 1].trim()
  const stateCode = stateString
    ? Object.entries(USStates).find(
        ([eachStateName, eachStateCode]) =>
          stateString.replace(' ', '').toLowerCase() === eachStateName.toLowerCase() ||
          stateString.toLowerCase() === eachStateCode.toLowerCase(),
      )?.[1]
    : undefined
  const city = cityStateParts.length > 1 ? cityStateParts.slice(0, -1).join(',').trim() : undefined
  return { city, stateCode }
}

function toSafeNumber(value: unknown): number | null {
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

function agencyMatchesState(agency: Agency, stateCode: USStates): boolean {
  const address = agency.address || ''
  const stateToken = stateCode.toUpperCase()
  const patterns = [new RegExp(`,\\s*${stateToken}(\\s|,|$)`, 'i'), new RegExp(`\\b${stateToken}\\b`, 'i')]
  return patterns.some((pattern) => pattern.test(address))
}

function focusSearchInput(input: HTMLInputElement | null): void {
  if (!input) {
    return
  }

  input.focus({ preventScroll: true })

  const valueLength = input.value.length
  try {
    input.setSelectionRange(valueLength, valueLength)
  } catch {
    // setSelectionRange is not supported on every input type.
  }

  if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Keyboard')) {
    void Keyboard.show()
  }
}

const DirectorySearch: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const navState = (location.state || {}) as DirectorySearchLocationState
  const [cityStateFilter] = useState(() => navState.cityStateSearchTerm ?? '')

  const tabFromQuery = useMemo(() => {
    const t = new URLSearchParams(location.search).get('tab')
    return t === 'agencies' ? 'agencies' : 'hospitals'
  }, [location.search])

  const [activeTab, setActiveTab] = useState<DirectoryTab>(tabFromQuery)
  const [query, setQuery] = useState(navState.initialQuery ?? '')
  const [results, setResults] = useState<(Hospital | Agency)[]>([])
  const [loading, setLoading] = useState(false)
  const [stateFilterOpen, setStateFilterOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const hospitalService = useMemo(() => new HospitalService(), [])
  const agencyService = useMemo(() => new AgencyService(), [])

  const { city, stateCode } = useMemo(() => parseCityStateForHospitalSearch(cityStateFilter), [cityStateFilter])
  const [selectedStateByTab, setSelectedStateByTab] = useState<{ agencies: USStates | ''; hospitals: USStates | '' }>({
    agencies: '',
    hospitals: stateCode || '',
  })

  const stateOptions = useMemo(
    () =>
      Object.entries(USStates)
        .map(([key, code]) => ({ code, name: formatStateEnumKey(key) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [],
  )

  useEffect(() => {
    setActiveTab(tabFromQuery)
  }, [tabFromQuery])

  useLayoutEffect(() => {
    focusSearchInput(inputRef.current)
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const trimmed = query.trim()
    if (trimmed.length < 1) {
      setResults([])
      setLoading(false)
      return
    }

    setLoading(true)
    debounceRef.current = setTimeout(() => {
      const finish = () => setLoading(false)
      if (activeTab === 'agencies') {
        agencyService
          .search({ autocomplete: true, page: 1, sort: 'az', term: trimmed })
          .then((r) => {
            const stateFilter = selectedStateByTab.agencies
            if (!stateFilter) {
              setResults(r.agencies)
              return
            }
            setResults(r.agencies.filter((agency) => agencyMatchesState(agency, stateFilter)))
          })
          .catch(() => setResults([]))
          .finally(finish)
      } else {
        hospitalService
          .search({
            autocomplete: true,
            city,
            page: 1,
            sort: 'az',
            state: selectedStateByTab.hospitals || stateCode,
            term: trimmed,
          })
          .then((r) => setResults(r.hospitals))
          .catch(() => setResults([]))
          .finally(finish)
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, activeTab, city, stateCode, selectedStateByTab, agencyService, hospitalService])

  const setTab = (tab: DirectoryTab) => {
    setActiveTab(tab)
    history.replace({
      pathname: '/directory-search',
      search: `?tab=${tab}`,
      state: location.state,
    })
  }

  const placeholder = activeTab === 'hospitals' ? 'Search hospitals…' : 'Search agencies…'
  const selectedStateCode = selectedStateByTab[activeTab]

  const exitToBrowseHome = useCallback(() => {
    setQuery('')
    setResults([])
    history.push({
      hash: activeTab === 'agencies' ? 'tab=agencies' : '',
      pathname: '/hospitals',
    })
  }, [history, activeTab])

  const runBrowseSearch = () => {
    const trimmed = query.trim()
    history.push({
      hash: activeTab === 'agencies' ? 'tab=agencies' : '',
      pathname: '/hospitals',
      state: {
        directorySearchTerm: trimmed,
        directoryTab: activeTab,
        scrollToBrowse: true,
      },
    })
  }

  /** So iOS / browser "back" lands on browse home, not /directory-search. */
  const navigateToHospitalDetail = useCallback(
    (permalink: string) => {
      inputRef.current?.blur()
      history.replace({ hash: '', pathname: '/hospitals' })
      history.push(`/hospitals/${permalink}`)
    },
    [history],
  )

  const navigateToAgencyDetail = useCallback(
    (permalink: string | null, id: number) => {
      inputRef.current?.blur()
      const pathSegment = permalink ?? String(id)
      history.replace({ hash: 'tab=agencies', pathname: '/hospitals' })
      history.push(`/agencies/${pathSegment}`)
    },
    [history],
  )

  return (
    <div className="flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-white">
      <div className="shrink-0 border-b border-gray-100 bg-white">
        <div className="mx-auto flex max-w-2xl items-center gap-2 px-4 pb-2 pt-safe-top-sm">
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full bg-gray-100 px-3 py-2.5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 shrink-0 text-gray-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              enterKeyHint="search"
              inputMode="search"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  runBrowseSearch()
                }
              }}
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-graphite-grey placeholder:text-gray-500 focus:outline-none focus:ring-0"
              placeholder={placeholder}
              aria-label={activeTab === 'hospitals' ? 'Search hospitals' : 'Search agencies'}
            />
            {query.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setQuery('')
                  setResults([])
                  focusSearchInput(inputRef.current)
                }}
                className="flex size-6 items-center justify-center rounded-full bg-gray-200 text-gray-500"
                aria-label="Clear search"
              >
                <span className="text-sm leading-none">×</span>
              </button>
            )}
          </div>
          <button
            type="button"
            className="shrink-0 px-1 text-base font-semibold text-royal-indigo"
            onMouseDown={(e) => e.preventDefault()}
            onClick={exitToBrowseHome}
          >
            Cancel
          </button>
        </div>

        <div className="mx-auto max-w-2xl px-4 pb-3">
          <div className="flex items-center gap-2">
            <div
              className="flex min-w-0 flex-1 justify-start rounded-full bg-gray-100 p-1"
              role="tablist"
              aria-label="Search category"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'hospitals'}
                onClick={() => setTab('hospitals')}
                className={`min-w-[108px] rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                  activeTab === 'hospitals' ? 'bg-orange text-graphite-grey shadow-sm' : 'text-gray-500 hover:bg-white'
                }`}
              >
                Hospitals
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'agencies'}
                onClick={() => setTab('agencies')}
                className={`min-w-[108px] rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                  activeTab === 'agencies' ? 'bg-orange text-graphite-grey shadow-sm' : 'text-gray-500 hover:bg-white'
                }`}
              >
                Agencies
              </button>
            </div>
            <button
              type="button"
              onClick={() => setStateFilterOpen(true)}
              className="inline-flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-500"
              aria-label={`Filter ${activeTab} by state`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M3 5a1 1 0 011-1h12a1 1 0 01.8 1.6L12 11.999V15a1 1 0 01-.553.894l-2 1A1 1 0 018 16v-4.001L3.2 5.6A1 1 0 013 5z" />
              </svg>
              <span>{selectedStateCode || 'Filter'}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-safe-bottom pt-2">
        {loading && query.trim().length > 0 && <p className="py-4 text-center text-sm text-gray-500">Searching…</p>}
        {!loading && query.trim().length > 0 && results.length === 0 && (
          <p className="py-6 text-center text-sm text-gray-600">
            No matches. Try another name or press Enter for full results.
          </p>
        )}
        {results.length > 0 && (
          <ul className="mx-auto max-w-2xl divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            {results.map((item) => (
              <li key={`${activeTab}-${item.id}`}>
                {(() => {
                  const ratingValue = toSafeNumber((item as Hospital | Agency).rating)
                  const reviewCountValue = toSafeNumber((item as Hospital | Agency).reviewCount)
                  return (
                    <Link
                      to={
                        activeTab === 'hospitals'
                          ? `/hospitals/${(item as Hospital).permalink}`
                          : `/agencies/${(item as Agency).permalink ?? (item as Agency).id}`
                      }
                      className="block px-4 py-3.5 hover:bg-gray-50"
                      onClick={(e) => {
                        e.preventDefault()
                        if (activeTab === 'hospitals') {
                          navigateToHospitalDetail((item as Hospital).permalink)
                        } else {
                          navigateToAgencyDetail((item as Agency).permalink, (item as Agency).id)
                        }
                      }}
                    >
                      <div
                        className="text-base font-medium text-graphite-grey"
                        dangerouslySetInnerHTML={{ __html: item.name }}
                      />
                      {activeTab === 'hospitals' && (
                        <div className="mt-0.5 flex items-center justify-between gap-3 text-sm text-gray-600">
                          <span className="truncate">
                            {[(item as Hospital).city, (item as Hospital).state?.code].filter(Boolean).join(', ') ||
                              ' '}
                          </span>
                          {ratingValue !== null && ratingValue > 0 && !hasNoReviews(reviewCountValue) ? (
                            <span className="shrink-0 text-[13px] text-gray-600">
                              <span className="mr-1 text-orange">★</span>
                              {ratingValue.toFixed(1)}
                              {reviewCountValue !== null && reviewCountValue > 0 && (
                                <span className="ml-1 text-gray-500">{reviewCountValue} reviews</span>
                              )}
                            </span>
                          ) : hasNoReviews(reviewCountValue) ? (
                            <NoReviewsYetBadge />
                          ) : null}
                        </div>
                      )}
                      {activeTab === 'agencies' && ratingValue !== null && ratingValue > 0 && (
                        <div className="mt-0.5 text-[13px] text-gray-600">
                          <span className="mr-1 text-orange">★</span>
                          {ratingValue.toFixed(1)}
                          {reviewCountValue !== null && reviewCountValue > 0 && (
                            <span className="ml-1 text-gray-500">{reviewCountValue} reviews</span>
                          )}
                        </div>
                      )}
                    </Link>
                  )
                })()}
              </li>
            ))}
          </ul>
        )}
      </div>

      {stateFilterOpen && (
        <div className="fixed inset-0 z-50 bg-black/30" onClick={() => setStateFilterOpen(false)} role="presentation">
          <div
            className="absolute bottom-0 left-0 right-0 flex h-[70vh] max-h-[70vh] flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal
            aria-label="Filter by state"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h3 className="text-base font-semibold text-graphite-grey">Filter by state</h3>
              <button
                type="button"
                className="text-sm font-semibold text-royal-indigo"
                onClick={() => {
                  setSelectedStateByTab((prev) => ({ ...prev, [activeTab]: '' }))
                  setStateFilterOpen(false)
                }}
              >
                Clear
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
              <button
                type="button"
                className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium ${
                  selectedStateCode === '' ? 'bg-orange/15 text-graphite-grey' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => {
                  setSelectedStateByTab((prev) => ({ ...prev, [activeTab]: '' }))
                  setStateFilterOpen(false)
                }}
              >
                All States
              </button>
              {stateOptions.map((state) => (
                <button
                  key={state.code}
                  type="button"
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium ${
                    selectedStateCode === state.code
                      ? 'bg-orange/15 text-graphite-grey'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    setSelectedStateByTab((prev) => ({ ...prev, [activeTab]: state.code }))
                    setStateFilterOpen(false)
                  }}
                >
                  {state.name} ({state.code})
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DirectorySearch
